import React from "react";

// Texts of the Google Business Profile categories page. The English version is
// written for US search (target keyword: "gbp categories"), not translated
// word for word.
const content = {
  fr: {
    seo: {
      title: "Recherche de catégories Google Business Profile | Outil gratuit GBP",
      description:
        "Trouvez et copiez les catégories Google Business Profile (GBP) en français, anglais, espagnol et italien. Outil gratuit pour optimiser votre fiche Google.",
      ogDescription:
        "Trouvez et copiez les catégories Google Business Profile (GBP) en français, anglais, espagnol et italien.",
    },
    locale: "fr-FR",
    defaultDataset: "fr",
    hero: {
      title: "Recherche de catégories Google Business Profile",
      subtitle:
        "Trouvez la catégorie GBP parfaite pour votre fiche Google. Recherchez par nom ou identifiant dans 5 langues.",
      cta: "Rechercher une catégorie",
    },
    tool: {
      languages: {
        fr: "Français",
        "en-US": "English (US)",
        "en-GB": "English (UK)",
        es: "Español",
        it: "Italiano",
      },
      searchPlaceholder: "Rechercher une catégorie… ex: restaurant, coiffeur, plombier",
      clearSearch: "Effacer la recherche",
      count: (n, searching) =>
        `${n.toLocaleString("fr-FR")} catégorie${n !== 1 ? "s" : ""}${
          searching ? " trouvée" + (n !== 1 ? "s" : "") : ""
        }`,
      colCategory: "Catégorie",
      colId: "Identifiant (gcid)",
      empty: (search) => <>Aucune catégorie trouvée pour «&nbsp;{search}&nbsp;»</>,
      emptyHint: "Essayez un terme plus court ou vérifiez l'orthographe.",
      copyTitle: "Copier le nom de la catégorie",
      copy: "Copier",
      copied: "Copié",
      loadMore: (remaining) => `Afficher plus de catégories (${remaining} restantes)`,
    },
    intro: {
      title: "Qu'est-ce que l'outil de recherche de catégories Google Business Profile ?",
      text: (
        <>
          Cet outil génère la{" "}
          <strong>liste complète des catégories Google Business Profile</strong> (les
          catégories utilisées pour les entreprises sur Google Maps). Vous pouvez générer
          la liste dans différentes combinaisons de langues et de pays. Les catégories par
          défaut dans GBP sont en anglais avec le pays États-Unis. Ces catégories sont
          exactement les mêmes que celles que vous voyez dans Google Maps.
        </>
      ),
      statCategories: "catégories disponibles dans Google Business Profile",
      statLanguages: "langues supportées : FR, EN-US, EN-GB, ES, IT",
      statMax: "nombre maximum de catégories pour une fiche GBP",
    },
    why: {
      title: "Pourquoi les catégories GBP sont-elles si importantes ?",
      paragraphs: [
        <>
          Après le <strong>nom de votre entreprise</strong>, la{" "}
          <strong>catégorie principale</strong> est le facteur le plus important pour un
          meilleur classement dans les résultats de recherche locale Google. Choisir la
          bonne catégorie détermine en grande partie pour quelles requêtes votre fiche
          apparaît dans le Local Pack et Google Maps.
        </>,
        <>
          Une catégorie mal choisie peut littéralement rendre votre fiche invisible sur des
          dizaines de mots-clés pertinents. À l'inverse, une catégorie bien ciblée booste
          votre visibilité immédiatement.
        </>,
      ],
    },
    tips: {
      title: "Bonnes pratiques pour choisir vos catégories Google Business Profile",
      items: [
        {
          icon: "🎯",
          title: "Choisissez la catégorie principale la plus spécifique",
          text: (
            <>
              Par exemple, si vous êtes un restaurant italien, choisissez «&nbsp;Restaurant
              italien&nbsp;» plutôt que simplement «&nbsp;Restaurant&nbsp;». Plus c'est
              précis, mieux Google comprend votre activité.
            </>
          ),
        },
        {
          icon: "➕",
          title: "Ajoutez 2 à 3 catégories secondaires pertinentes",
          text: "Complétez votre catégorie principale avec des catégories additionnelles qui décrivent d'autres aspects de votre activité. Mais ne dépassez jamais 5 à 6 catégories au total.",
        },
        {
          icon: "⚠️",
          title: "N'abusez pas des catégories",
          text: "Le nombre maximum de catégories est de 10, mais ajouter des catégories non pertinentes peut diluer votre signal et nuire à votre classement. Privilégiez la qualité à la quantité.",
        },
        {
          icon: "🔄",
          title: "Surveillez régulièrement les nouvelles catégories",
          text: "Google ajoute et supprime des catégories chaque mois. Gardez un œil sur les nouvelles catégories qui pourraient mieux décrire votre activité.",
        },
        {
          icon: "📊",
          title: "Testez et mesurez l'impact",
          text: "Ajoutez ou supprimez des catégories secondaires et observez comment votre classement et vos leads évoluent. C'est une bonne pratique de A/B tester vos catégories.",
        },
      ],
    },
    howMany: {
      title: "Combien de catégories devez-vous ajouter à votre fiche ?",
      scale: {
        bad: { range: "1 seule", label: "Insuffisant" },
        good: { range: "3 – 5", label: "Idéal" },
        ok: { range: "6 – 8", label: "Acceptable" },
        sus: { range: "9 – 10", label: "Risqué" },
      },
      text: (
        <>
          La meilleure pratique est de choisir la catégorie la plus pertinente comme
          catégorie principale et d'ajouter <strong>2 à 3 catégories additionnelles</strong>{" "}
          qui décrivent également votre activité, sans dépasser 5 à 6 catégories au total.
          Ajouter trop de catégories non pertinentes peut envoyer des signaux confus à
          Google et diluer votre pertinence pour vos requêtes clés.
        </>
      ),
    },
    impact: {
      title: "Comment les catégories GBP impactent votre SEO local ?",
      factors: [
        {
          icon: "📍",
          title: "Visibilité Local Pack",
          text: "La catégorie principale détermine dans quel Local Pack votre fiche apparaît.",
        },
        {
          icon: "🔍",
          title: "Requêtes de découverte",
          text: "Les catégories définissent pour quelles recherches génériques votre fiche est éligible.",
        },
        {
          icon: "🏆",
          title: "Avantage concurrentiel",
          text: "Une catégorie plus spécifique que vos concurrents vous donne un avantage de classement.",
        },
      ],
      text: (
        <>
          Google utilise vos catégories comme signal principal pour déterminer la
          pertinence de votre fiche face à une requête. Un choix stratégique de catégories
          est donc <strong>indispensable</strong> pour toute stratégie de SEO local
          efficace.
        </>
      ),
    },
    faq: {
      title: "FAQ — Catégories Google Business Profile",
      items: [
        {
          q: "Peut-on changer sa catégorie principale GBP ?",
          a: "Oui, vous pouvez modifier votre catégorie principale à tout moment depuis votre tableau de bord Google Business Profile. Attention cependant : un changement de catégorie principale peut impacter temporairement votre classement le temps que Google réindexe votre fiche.",
        },
        {
          q: "À quelle fréquence Google met-il à jour les catégories ?",
          a: "Google ajoute et supprime des catégories tous les mois. Il est important de vérifier régulièrement si de nouvelles catégories plus pertinentes sont disponibles pour votre activité.",
        },
        {
          q: "Ma catégorie n'existe pas dans la liste, que faire ?",
          a: "Google ne permet pas de créer des catégories personnalisées. Choisissez la catégorie existante qui décrit le mieux votre activité principale. Vous pouvez compléter avec des catégories secondaires plus larges.",
        },
      ],
    },
  },

  en: {
    seo: {
      title: "Google Business Profile Categories List | Localranker",
      description:
        "Search the full list of Google Business Profile categories (formerly Google My Business). Find the right GBP category, then copy its name in one click.",
    },
    locale: "en-US",
    defaultDataset: "en-US",
    hero: {
      title: "Google Business Profile Categories Search",
      subtitle:
        "Find the right GBP category for your Google listing. Search by name or category ID (gcid) in 5 languages.",
      cta: "Search categories",
    },
    tool: {
      languages: {
        fr: "French",
        "en-US": "English (US)",
        "en-GB": "English (UK)",
        es: "Spanish",
        it: "Italian",
      },
      searchPlaceholder: "Search a category… e.g. restaurant, hair salon, plumber",
      clearSearch: "Clear search",
      count: (n, searching) =>
        `${n.toLocaleString("en-US")} ${n !== 1 ? "categories" : "category"}${
          searching ? " found" : ""
        }`,
      colCategory: "Category",
      colId: "Category ID (gcid)",
      empty: (search) => <>No categories found for “{search}”</>,
      emptyHint: "Try a shorter term or check the spelling.",
      copyTitle: "Copy the category name",
      copy: "Copy",
      copied: "Copied",
      loadMore: (remaining) =>
        `Show more categories (${remaining.toLocaleString("en-US")} left)`,
    },
    intro: {
      title: "The full list of Google business categories, searchable",
      text: (
        <>
          This tool gives you the{" "}
          <strong>full list of Google Business Profile categories</strong>: the business
          categories Google uses on Maps and in local search. You may know them as Google
          My Business categories, the platform's former name. Switch between languages and
          countries to see how each category is labeled in that market. Google's default
          set is English for the United States, which is what this page loads first. The
          names are exactly the ones you see in Google Maps and in your Business Profile
          dashboard.
        </>
      ),
      statCategories: "categories available in Google Business Profile",
      statLanguages: "supported languages: FR, EN-US, EN-GB, ES, IT",
      statMax: "categories max on a single Business Profile",
    },
    why: {
      title: "Why do GBP categories matter so much?",
      paragraphs: [
        <>
          After your <strong>business name</strong>, your{" "}
          <strong>primary category</strong> is the most important factor for ranking in
          Google's local results. It largely decides which searches your profile shows up
          for in the Local Pack and on Google Maps.
        </>,
        <>
          Pick the wrong category and your profile can be invisible for dozens of relevant
          keywords. Pick a well-targeted one and your visibility improves right away.
        </>,
      ],
    },
    tips: {
      title: "How to choose your primary and secondary categories",
      items: [
        {
          icon: "🎯",
          title: "Pick the most specific primary category",
          text: (
            <>
              If you run an Italian restaurant, choose “Italian restaurant” rather than just
              “Restaurant.” The more precise you are, the better Google understands your
              business.
            </>
          ),
        },
        {
          icon: "➕",
          title: "Add 2 to 3 relevant secondary categories",
          text: "Use additional categories to cover other parts of your business, on top of your primary category. Just don't go beyond 5 or 6 categories in total.",
        },
        {
          icon: "⚠️",
          title: "Don't overdo it",
          text: "You can add up to 10 categories, but irrelevant ones dilute your signal and can hurt your rankings. Quality beats quantity.",
        },
        {
          icon: "🔄",
          title: "Check for new categories regularly",
          text: "Google adds and removes categories regularly. Keep an eye out for new ones that describe your business better.",
        },
        {
          icon: "📊",
          title: "Test and measure the impact",
          text: "Add or remove secondary categories and watch how your rankings and leads change. Testing your categories is a good habit.",
        },
      ],
    },
    howMany: {
      title: "How many categories should you add to your profile?",
      scale: {
        bad: { range: "Just 1", label: "Too few" },
        good: { range: "3 – 5", label: "Ideal" },
        ok: { range: "6 – 8", label: "Acceptable" },
        sus: { range: "9 – 10", label: "Risky" },
      },
      text: (
        <>
          Best practice: use the most relevant category as your primary category, then add{" "}
          <strong>2 to 3 additional categories</strong> that also describe your business,
          staying under 5 or 6 in total. Too many irrelevant categories send mixed signals
          to Google and dilute your relevance for the searches that matter most.
        </>
      ),
    },
    impact: {
      title: "How GBP categories affect your local SEO",
      factors: [
        {
          icon: "📍",
          title: "Local Pack visibility",
          text: "Your primary category decides which Local Pack your profile can appear in.",
        },
        {
          icon: "🔍",
          title: "Discovery searches",
          text: "Categories define which generic searches (like “plumber near me”) your profile is eligible for.",
        },
        {
          icon: "🏆",
          title: "Competitive edge",
          text: "A more specific category than your competitors' can give you a ranking advantage.",
        },
      ],
      text: (
        <>
          Google uses your categories as a key signal to decide how relevant your profile is
          to a search. Choosing them strategically is <strong>essential</strong> for any
          effective local SEO strategy.
        </>
      ),
    },
    faq: {
      title: "FAQ: Google Business Profile categories",
      items: [
        {
          q: "Are Google My Business categories the same as GBP categories?",
          a: "Yes. Google renamed Google My Business to Google Business Profile, but the categories themselves did not change. Any list of Google My Business categories you come across refers to the same set.",
        },
        {
          q: "Can I change my primary GBP category?",
          a: "Yes, you can change your primary category at any time from your Google Business Profile dashboard. Keep in mind that a new primary category can temporarily affect your rankings while Google reprocesses your profile.",
        },
        {
          q: "How often does Google update its categories?",
          a: "Google adds and removes categories regularly. It's worth checking from time to time whether a newer, more relevant category is available for your business.",
        },
        {
          q: "What if my category isn't on the list?",
          a: "Google doesn't allow custom categories. Pick the existing category that best describes your main activity, and complement it with broader secondary categories.",
        },
      ],
    },
  },
};

export default content;
